import { useSupportSessionSwitch } from './SupportSessionContext';
import { useRef, useState } from 'react';
import { useDialogFocus } from './useDialogFocus';
import { X } from 'lucide-react';
import { adminWorkspaceApi, type AdminPerson, type AdminScope } from './api';

export default function AdminImpersonationDialog({
  scope,
  scopeId,
  name,
  candidates,
  onClose,
}: {
  scope: AdminScope;
  scopeId: string;
  name: string;
  candidates: AdminPerson[];
  onClose: () => void;
}) {
  const switchSession = useSupportSessionSwitch();
  const [targetId, setTargetId] = useState(candidates[0]?.id || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef<HTMLElement>(null);
  useDialogFocus(dialog, onClose, busy);
  const target = candidates.find((user) => user.id === targetId);
  return (
    <div className="admin-dialog-backdrop">
      <section
        className="admin-dialog"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-support-title"
      >
        <div className="admin-section-heading">
          <div>
            <p className="adviser-eyebrow">Attributed support session</p>
            <h2 id="admin-support-title">Open {name} as a user</h2>
          </div>
          <button
            type="button"
            className="admin-icon-button"
            aria-label="Close support session"
            disabled={busy}
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        <p>
          You will use this person's actual account permissions. The session records your identity
          and reason, and shows a visible banner until you exit.
        </p>
        <p className="adviser-notice">
          Your administrator login stays signed in. Support uses this person’s application access
          for up to 30 minutes in this tab. Exit support to return to administration.
        </p>
        {!candidates.length ? (
          <p className="adviser-notice">
            No eligible account is connected to this record. Open its owner or adviser practice to
            review access.
          </p>
        ) : (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!target || reason.trim().length < 3) return;
              setBusy(true);
              setError('');
              try {
                const result = await adminWorkspaceApi.startImpersonation(
                  scope,
                  scopeId,
                  reason.trim(),
                  target.id
                );
                await switchSession(result);
              } catch (err) {
                setError(
                  err instanceof Error ? err.message : 'Unable to open this support session.'
                );
                setBusy(false);
              }
            }}
          >
            <label>
              Connected account
              <select
                value={targetId}
                disabled={busy}
                onChange={(event) => setTargetId(event.target.value)}
              >
                {candidates.map((user) => (
                  <option value={user.id} key={user.id}>
                    {user.name} · {user.email}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Support reason
              <textarea
                required
                minLength={3}
                maxLength={2000}
                rows={3}
                value={reason}
                disabled={busy}
                onChange={(event) => setReason(event.target.value)}
                placeholder="What you are helping this person with"
              />
            </label>
            {error && (
              <p className="adviser-error" role="alert">
                {error}
              </p>
            )}
            <div className="admin-actions">
              <button
                className="adviser-button"
                type="submit"
                disabled={busy || !target || reason.trim().length < 3}
              >
                {busy ? 'Opening support session…' : `Open as ${target?.name || 'selected user'}`}
              </button>
              <button
                className="adviser-button adviser-button-secondary"
                type="button"
                disabled={busy}
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
